import React, { useState, useCallback, useMemo } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import dayjs from "dayjs";
import {
  has,
  uniq,
  isEmpty,
  map,
  remove,
  difference,
  pull,
  findIndex,
} from "lodash";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DiscountSearch, {
  groupBy,
} from "../DiscountAndPlanogram/DiscountSearch";
import OSOAccordion from "../OSOAccordion";
import { ENTITY_TYPES, prettyNumericRound } from "../../utils";
import {
  getAdjustedQty,
  getTierBasedCost,
  getTierLevelForQty,
  getTierBasedItemValues,
} from "../../utils/pogCalculation";
import { getItemLevelSummations } from "../../utils/getSummations";
import { validItem } from "../../utils/itemValidator";
import ActionsFooter from "../ActionsFooter";
import { GeneratePOByMap } from "../ActionsFooter/OrderFooterActions";
import useRequest from "../../hooks/useRequest";
import useStableCallback from "../../hooks/useStableCallback";
import usePermissions from "../../hooks/usePermissions";
import {
  setGlobalLoader,
  showNotifier,
  setSettings,
  setRestrictedPog,
  calculateAvailableCreditLimit,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../store/globalSlice";
import configData from "../../config.js";
import {
  getAutoUpdatedPogItems,
  autoResetPogFields,
  updateNotes,
  getCountByKey,
  getPogPrefillValues,
} from "./utils";
import { SCOPES } from "../../permissions/permissions-map";

const DEFAULT_MULTIPLIER = "";

const ManageStore = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { requestMaker } = useRequest();
  const { hasPermission } = usePermissions();
  const store = useSelector((store) => store.global.store);
  const user = useSelector((store) => store.global.user);
  const [flushStateKey, setFlushStateKey] = useState(0);
  const [resetFooterKey, setResetFooterKey] = useState(0);
  //const [data, setData] = useState({ ...mockPog });
  //const [data, setData] = useState(mockData);
  const [data, setData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [unselectedPogs, setUnselectedPogs] = useState({});
  const [originalUnselectedPogs, setOriginalUnselectedPogs] = useState({});
  const [viewPogBy, setViewPogBy] = useState(groupBy[0]);
  const [filterData, setFilterData] = useState([]);
  const [serverSelections, setServerSelections] = useState({});
  const [originalServerSelections, setOriginalServerSelections] = useState({});

  const currentEntity = useMemo(() => {
    let currentEntityType = "";
    switch (pathname) {
      case "/discounting":
        currentEntityType = ENTITY_TYPES.discount;
        break;
      case "/manage-planograms":
        currentEntityType = ENTITY_TYPES.planogram;
        break;
      case "/manage-orders":
        currentEntityType = ENTITY_TYPES.orders;
        break;
      default:
        currentEntityType = "";
    }
    dispatch(setSettings({ entityType: currentEntityType }));
    return currentEntityType;
  }, [pathname, dispatch]);

  const getUpdatedItemList = useStableCallback(
    (
      pogScope,
      pogId,
      itemList = [],
      multiplier = 1,
      initializing = false,
      initialSelections = {}
    ) => {
      let restrictedItems = 0;
      const updatedList = [];
      for (let i = 0; i < itemList.length; i++) {
        const item = {
          ...itemList[i],
          multiplier: initializing ? itemList[i]?.multiplier : multiplier,
        };
        const adjQty =
          currentEntity === ENTITY_TYPES.orders
            ? item?.adjqty || 0
            : item?.adjqty ||
              getAdjustedQty(item?.multiplier, item?.pogqty, item?.missingskus);

        const { pogCost, adjCost, totalCost } = getTierBasedItemValues(
          item,
          adjQty,
          currentEntity === ENTITY_TYPES.orders
        );
        const orderCost =
          currentEntity === ENTITY_TYPES.orders
            ? item?.SavedOrderCost ?? adjCost
            : adjCost;
        const itemData = {
          ...item,
          adjqty: adjQty,
          pogcost: pogCost,
          adjcost: adjCost,
          totalCost,
          orderQty: adjQty,
          orderCost: orderCost,
        };
        if (ENTITY_TYPES.orders === currentEntity && !validItem(itemData)) {
          restrictedItems = restrictedItems + 1;
        }
        updatedList.push(itemData);
      }
      let selections = [];
      if (!initializing) {
        if (has(serverSelections, [pogScope, pogId])) {
          selections = serverSelections[pogScope][pogId] ?? [];
        }
      } else {
        if (
          ENTITY_TYPES.orders === currentEntity &&
          itemList.length === restrictedItems
        ) {
          dispatch(setRestrictedPog({ scope: pogScope, pogId }));
        }
        if (has(initialSelections, [pogScope, pogId])) {
          selections = initialSelections[pogScope][pogId] ?? [];
        }
      }
      const summations = getItemLevelSummations(updatedList, selections);
      return [summations, updatedList];
    }
  );

  const getServerPreSelections = useCallback((details) => {
    const serverSelections = Object.keys(details).reduce((acc, scope) => {
      const scopeList = details[scope] ?? [];
      for (let i = 0; i < scopeList?.length; i++) {
        const parentItem = scopeList[i];
        const childItems = scopeList[i]?.subRows ?? [];
        const parentSelections = acc[parentItem.id] ?? [];
        if (Boolean(parentItem.Selected)) {
          acc = {
            ...acc,
            [scope]: {
              ...acc[scope],
              [parentItem.id]: [...parentSelections, parentItem.id],
            },
          };
        } else {
          // Used when select all pogs action is performed.
          setUnselectedPogs((prevState) => {
            const currentDelta = {
              ...prevState,
              [scope]: has(prevState, scope)
                ? uniq([...prevState[scope], parentItem.id])
                : [parentItem.id],
            };
            //Required for resetting.
            setOriginalUnselectedPogs(currentDelta);
            return currentDelta;
          });
        }

        for (let j = 0; j < childItems.length; j++) {
          const updatedParentSelections = has(acc[scope], parentItem.id)
            ? acc[scope][parentItem.id]
            : [];
          if (Boolean(childItems[j].Selected)) {
            const uniqChildIds = uniq([
              ...updatedParentSelections,
              parentItem.id,
              childItems[j].id,
            ]);
            acc = {
              ...acc,
              [scope]: {
                ...acc[scope],
                [parentItem.id]: [...uniqChildIds],
              },
            };
          }
        }
      }
      return acc;
    }, {});
    return serverSelections;
  }, []);

  const deltaServerSelections = useMemo(() => {
    let newDelta = {};
    const scopeList = Object.keys(originalServerSelections);
    if (!scopeList.length) {
      return newDelta;
    }
    for (let j = 0; j < scopeList.length; j++) {
      const scope = scopeList[j];
      const pogList = Object.keys(originalServerSelections[scope]);
      for (let i = 0; i < pogList.length; i++) {
        const pogId = pogList[i];
        newDelta = has(newDelta, scope)
          ? { ...newDelta, [scope]: { ...newDelta[scope] } }
          : newDelta;
        // Item level delta
        if (has(serverSelections[scope], pogId)) {
          const itemList = serverSelections[scope][pogId];
          const deltaItems = difference(
            originalServerSelections[scope][pogId],
            itemList
          );
          if (deltaItems.length) {
            newDelta = {
              ...newDelta,
              [scope]: {
                ...newDelta[scope],
                [pogId]: [...pull(deltaItems, pogId)],
              },
            };
          }
        } else {
          // Pog level delta
          const items = [...originalServerSelections[scope][pogId]];
          newDelta = {
            ...newDelta,
            [scope]: {
              ...newDelta[scope],
              [pogId]: [...pull(items, pogId)],
            },
          };
        }
      }
    }
    return newDelta;
  }, [serverSelections, originalServerSelections]);

  const recalculatePogSummations = useStableCallback(
    (
      scope,
      pogId,
      selections = [],
      isAllUpdate = false,
      isAllChecked = false
    ) => {
      if (isAllUpdate) {
        const pogList = data[scope];
        let changedData = {};
        for (let i = 0; i < pogList.length; i++) {
          changedData = has(changedData, scope) ? changedData : { [scope]: [] };
          const pog = pogList[i];
          let summations = {
            pogCost: 0,
            adjQty: 0,
            adjCost: 0,
            pogQty: 0,
            missingSkus: 0,
            orderQty: 0,
            orderCost: 0,
          };
          if (isAllChecked) {
            const itemList = pog?.subRows ?? [];
            const itemIdList = map(itemList, "id");
            summations = getItemLevelSummations(itemList, itemIdList);
            changedData = {
              ...changedData,
              [scope]: [
                ...changedData[scope],
                {
                  ...pog,
                  adjqty: summations.adjQty,
                  adjcost: summations.adjCost,
                  pogcost: summations.pogCost,
                  totalCost: summations.totalCost,
                  missingskus: summations.missingSkus,
                  orderQty: summations.orderQty,
                  orderCost: summations.orderCost,
                },
              ],
            };
          } else {
            changedData = {
              ...changedData,
              [scope]: [
                ...changedData[scope],
                {
                  ...pog,
                  adjqty: summations.adjQty,
                  adjcost: summations.adjCost,
                  pogcost: summations.pogCost,
                  totalCost: summations.totalCost,
                  missingskus: summations.missingSkus,
                  orderQty: summations.orderQty,
                  orderCost: summations.orderCost,
                },
              ],
            };
          }
        }
        setData((prevData) => ({
          ...prevData,
          ...changedData,
        }));
      } else {
        const pog = data[scope].find((pogData) => pogData.id === pogId);
        // Update pog level summations due to item selection.
        if (!isEmpty(pog)) {
          const itemList = pog?.subRows ?? [];
          const {
            pogCost,
            adjQty,
            adjCost,
            missingSkus,
            totalCost,
            orderQty,
            orderCost,
          } = getItemLevelSummations(itemList, selections);
          setData((details) => {
            const pogList = [...details[scope]];
            const pogIndex = findIndex(pogList, ["id", pog.id]);

            // Checks if new selected item details are same across selections
            const matches = getPogPrefillValues(selections, itemList);

            pogList.splice(pogIndex, 1, {
              ...pog,
              adjqty: adjQty,
              adjcost: adjCost,
              pogcost: pogCost,
              totalCost,
              missingskus: missingSkus,
              multiplier: matches.multiplier,
              PlannerAction: matches.PlannerAction,
              RetailerAction: matches.RetailerAction,
              plannernotesid: matches.plannernotesid,
              retailernotesid: matches.retailernotesid,
              orderQty: orderQty,
              orderCost: orderCost,
              subRows: [...itemList],
            });
            return {
              ...details,
              [scope]: [...pogList],
            };
          });
        }
      }
    }
  );

  const updateSelections = useStableCallback(
    (newSelections, parentId, scope) => {
      setServerSelections((prevSelections) => {
        let oldSelections = { ...prevSelections };
        let updatedSelections = { ...oldSelections[scope] };
        if (!newSelections.length) {
          // Used when select all pogs action is performed.
          setUnselectedPogs((prevState) => ({
            ...prevState,
            [scope]: has(prevState, scope)
              ? uniq([...prevState[scope], parentId])
              : [parentId],
          }));
          delete updatedSelections[parentId];
        } else {
          setUnselectedPogs((prevState) => {
            let state = { ...prevState };
            const updatedState =
              prevState[scope]?.filter((id) => id !== parentId) ?? [];
            if (!updatedState.length) {
              delete state[scope];
            } else {
              state = {
                ...state,
                [scope]: updatedState,
              };
            }
            return state;
          });
          updatedSelections = {
            ...updatedSelections,
            [parentId]: [...newSelections],
          };
        }
        let updatedScope = {
          [scope]: {
            ...updatedSelections,
          },
        };

        if (isEmpty(updatedSelections)) {
          delete oldSelections[scope];
          delete updatedScope[scope];
        }
        return {
          ...oldSelections,
          ...updatedScope,
        };
      });
      recalculatePogSummations(scope, parentId, newSelections, false, false);
    }
  );

  const handleSelectAllSubAccordion = useStableCallback(
    (selectedScope, isChecked) => {
      if (isChecked) {
        if (data[selectedScope]) {
          const pogList = data[selectedScope];
          let delta = {};
          for (let i = 0; i < pogList.length; i++) {
            const pog = pogList[i];
            const pogId = pog?.id;
            let itemIds = [];
            if (ENTITY_TYPES.orders === currentEntity) {
              itemIds = map(pog?.subRows, validItem).filter((i) => !!i);
            } else {
              itemIds = map(pog?.subRows, "id") ?? [];
            }
            const selections = [pogId, ...itemIds];
            delta = {
              ...delta,
              [pogId]: selections.length === 1 ? [] : [...selections],
            };
            if (unselectedPogs[selectedScope]) {
              setUnselectedPogs((prevState) => {
                let state = { ...prevState };
                if (!isEmpty(state) && state[selectedScope]) {
                  const unSelections = [...state[selectedScope]];
                  remove(unSelections, (i) => i === pogId);
                  if (!unSelections.length) {
                    delete state[selectedScope];
                  } else {
                    state = {
                      ...state,
                      [selectedScope]: [...unSelections],
                    };
                  }
                }
                return state;
              });
            }
          }
          setServerSelections((prevSelections) => ({
            ...prevSelections,
            [selectedScope]: {
              ...prevSelections[selectedScope],
              ...delta,
            },
          }));
        }
      } else {
        const pogList = data[selectedScope];
        const deltaPogIds = map(pogList, "id");
        setUnselectedPogs((prevState) => ({
          ...prevState,
          [selectedScope]: [...deltaPogIds],
        }));
        setServerSelections((prevSelections) => {
          const updatedSelections = { ...prevSelections };
          delete updatedSelections[selectedScope];
          return updatedSelections;
        });
      }
      recalculatePogSummations(selectedScope, null, null, true, isChecked);
    }
  );

  const handleSelectAllAccordion = useCallback(
    (event) => {
      const { checked } = event?.target;
      const scopeList = Object.keys(data);
      for (let x = 0; x < scopeList.length; x++) {
        const scope = scopeList[x];
        handleSelectAllSubAccordion(scope, checked);
      }
    },
    [data, handleSelectAllSubAccordion]
  );

  const handleGlobalMultiplierChange = useCallback(
    (
      scope,
      multiplier,
      initializing = false,
      searchData = {},
      initialSelections = {}
    ) => {
      const currentData = initializing ? searchData : data;
      const multiplierValue = initializing ? null : multiplier;
      let updatedPogs = [];
      const pogList = currentData[scope] ?? [];
      for (let i = 0; i < pogList?.length; i++) {
        const pog = pogList[i] ?? {};
        const pogMultiplier = initializing
          ? pog?.multiplier ?? DEFAULT_MULTIPLIER
          : multiplierValue;
        const itemList = pog?.subRows ?? [];
        const [
          { pogCost, adjQty, adjCost, missingSkus, totalCost, orderCost },
          updatedItemList,
        ] = getUpdatedItemList(
          scope,
          pog.id,
          itemList,
          pogMultiplier,
          initializing,
          initialSelections
        );
        updatedPogs.push({
          ...pog,
          multiplier: pogMultiplier,
          adjqty: adjQty,
          adjcost: adjCost,
          pogcost: pogCost,
          totalCost,
          missingskus: missingSkus,
          orderQty: adjQty,
          orderCost: orderCost,
          subRows: [...updatedItemList],
        });
      }
      setData((prevData) => {
        return {
          ...prevData,
          [scope]: [...updatedPogs],
        };
      });
      if (initializing) {
        return {
          [scope]: [...updatedPogs],
        };
      } else {
        return;
      }
    },
    [data, getUpdatedItemList]
  );

  const handleProcessedData = useStableCallback((key, updatedRow, index) => {
    let itemMultiplierChanged = false;
    let itemAdjQtyChanged = false;
    let itemNotesChanged = false;
    setData((details) => {
      const pog = { ...details[key][index] } ?? {};
      const updatedList = [];
      if (pog.id === updatedRow.id) {
        if (
          has(updatedRow, "multiplier") &&
          pog.multiplier !== updatedRow.multiplier
        ) {
          const itemList = pog?.subRows ?? [];
          const [
            { pogCost, adjQty, adjCost, missingSkus, totalCost },
            updatedItemList,
          ] = getUpdatedItemList(key, pog.id, itemList, updatedRow.multiplier);
          updatedList.push({
            ...pog,
            ...updatedRow,
            adjqty: adjQty,
            adjcost: adjCost,
            pogcost: pogCost,
            totalCost,
            missingskus: missingSkus,
            subRows: [...updatedItemList],
          });
        } else {
          const currentSelections = has(serverSelections, key)
            ? serverSelections[key][pog.id]
            : [];
          const [pogChange, updateItemList] = getAutoUpdatedPogItems(
            pog,
            updatedRow,
            currentSelections
          );
          updatedList.push({
            ...pog,
            ...updatedRow,
            ...pogChange,
            subRows: [...updateItemList],
          });
        }
      } else {
        const itemList = pog?.subRows ?? [];
        const updatedItemList = [];
        for (let i = 0; i < itemList.length; i++) {
          const item = itemList[i];
          if (item.id === updatedRow.id) {
            const updatedItem = {
              ...item,
              ...updatedRow,
            };
            if (
              has(updatedRow, "multiplier") &&
              item.multiplier !== updatedRow?.multiplier
            ) {
              const adjQty = getAdjustedQty(
                updatedItem?.multiplier,
                updatedItem?.pogqty,
                updatedItem?.missingskus
              );
              const { pogCost, adjCost, totalCost } = getTierBasedItemValues(
                updatedItem,
                adjQty
              );
              updatedItemList.push({
                ...updatedItem,
                adjqty: adjQty,
                adjcost: adjCost,
                pogcost: pogCost,
                totalCost,
              });
              itemMultiplierChanged = true; // used to recalculate pog values.
            } else if (
              has(updatedRow, "adjqty") &&
              item.adjqty !== updatedRow?.adjqty
            ) {
              const { pogCost, adjCost, totalCost } = getTierBasedItemValues(
                updatedItem,
                updatedItem?.adjqty
              );
              updatedItemList.push({
                ...updatedItem,
                adjcost: adjCost,
                pogcost: pogCost,
                totalCost,
              });
              itemAdjQtyChanged = true;
            } else {
              itemNotesChanged = true;
              // Item level notes related fields set/reset based on id value.
              const changes = updateNotes(pog, updatedRow);
              updatedItemList.push({ ...updatedItem, ...changes });
            }
          } else {
            updatedItemList.push(item);
          }
        }
        updatedList.push({
          ...pog,
          subRows: [...updatedItemList],
        });
      }
      const pogList = [...details[key]];
      const updatedPogs = [];
      for (let j = 0; j < pogList.length; j++) {
        if (j === index) {
          let updatedPog = {
            ...updatedList[0],
          };
          if (itemMultiplierChanged) {
            const multiplierCount = getCountByKey(
              updatedPog,
              "multiplier",
              updatedRow?.multiplier
            );
            const selectionsCount = has(serverSelections, key)
              ? serverSelections[key][updatedPog?.id]?.length
              : 0;
            updatedPog = {
              ...updatedPog,
              multiplier:
                multiplierCount >= selectionsCount - 1
                  ? updatedRow.multiplier
                  : "",
            };
          }
          if (itemNotesChanged) {
            const scopedSelections = has(serverSelections, key)
              ? serverSelections[key][updatedPog?.id]
              : [];
            const noteFieldChanges = autoResetPogFields(
              itemNotesChanged,
              updatedRow,
              updatedPog,
              scopedSelections
            );
            updatedPog = {
              ...updatedPog,
              ...noteFieldChanges,
              subRows: [...updatedList[0]?.subRows],
            };
          }
          updatedPogs.push(updatedPog);
          itemNotesChanged = false;
          continue;
        }
        updatedPogs.push(pogList[j]);
      }
      return {
        ...details,
        [key]: [...updatedPogs],
      };
    });
    if (itemMultiplierChanged || itemAdjQtyChanged) {
      const scope = data[key];
      const pog = scope[index];
      // Throttle to sync for updated data.
      setTimeout(() => {
        recalculatePogSummations(
          key,
          pog.id,
          serverSelections[key][pog.id] ?? []
        );
      });
    }
  });

  const handleSearchData = useStableCallback((searchData) => {
    if (!isEmpty(searchData)) {
      const serverSelections = getServerPreSelections(searchData);
      const scopes = Object.keys(searchData) ?? [];
      let calculatedData = {};
      // Do initial calculation based on multiplier(if no multiplier found it will assume default as 1)
      for (let i = 0; i < scopes.length; i++) {
        const data = handleGlobalMultiplierChange(
          scopes[i],
          null,
          true,
          searchData,
          serverSelections
        );
        calculatedData = {
          ...calculatedData,
          ...data,
        };
      }
      setOriginalData(calculatedData);
      setServerSelections(serverSelections);
      setOriginalServerSelections(serverSelections);
    } else {
      setData({});
      setOriginalData({});
      setServerSelections({});
      setOriginalServerSelections({});
      dispatch(setRestrictedPog({ scope: "", pogId: "", clear: true }));
    }
  });

  const handleViewSearchBy = useCallback((data) => {
    setViewPogBy(data);
  }, []);

  const handleFilterData = useCallback((data) => {
    setFilterData(data);
  }, []);

  const getMergedData = useCallback(
    (updatedData) => {
      let mergedData = {};
      const selectedScopes = Object.keys(serverSelections);
      const dataScopes = Object.keys(data);
      for (let i = 0; i < dataScopes.length; i++) {
        const currentScope = dataScopes[i];
        mergedData = has(mergedData, currentScope)
          ? mergedData
          : { ...mergedData, [currentScope]: [] };
        if (selectedScopes.includes(currentScope)) {
          const pogList = data[currentScope] ?? [];
          const updatedPogList = updatedData[currentScope] ?? [];
          const selectedPogList = serverSelections[currentScope] ?? {};

          for (let j = 0; j < pogList.length; j++) {
            const pog = pogList[j];
            if (has(selectedPogList, pog.id)) {
              const updatedPog = updatedPogList.find(
                (upog) => upog.id === pog.id
              );
              // which items are not selected set them to selected 0;
              const itemsSelected = selectedPogList[pog.id] ?? [];
              const itemList = pog?.subRows ?? [];
              let newItemList = [];
              for (let k = 0; k < itemList.length; k++) {
                const item = itemList[k];
                if (itemsSelected.includes(item.id)) {
                  const updatedItem = updatedPog?.subRows.find(
                    (upog) => upog.id === item.id
                  );
                  newItemList.push({
                    ...updatedItem,
                  });
                } else {
                  // item not selected so setting 0 and using original item data.
                  newItemList.push({
                    ...item,
                    Selected: 0,
                    multiplier: null,
                  });
                }
              }
              mergedData = {
                ...mergedData,
                [currentScope]: [
                  ...mergedData[currentScope],
                  {
                    ...updatedPog,
                    subRows: [...newItemList],
                  },
                ],
              };
            } else {
              const itemList = pog?.subRows?.map((itm) => ({
                ...itm,
                Selected: 0,
              }));
              mergedData = {
                ...mergedData,
                [currentScope]: [
                  ...mergedData[currentScope],
                  {
                    ...pog,
                    Selected: 0,
                    subRows: [...itemList],
                  },
                ],
              };
            }
          }
        } else {
          const pogList = data[currentScope];
          for (let j = 0; j < pogList.length; j++) {
            const pog = pogList[j];
            const itemList =
              pog?.subRows?.map((itm) => ({
                ...itm,
                Selected: 0,
              })) ?? [];
            mergedData = {
              ...mergedData,
              [currentScope]: [
                ...mergedData[currentScope],
                {
                  ...pog,
                  Selected: 0,
                  subRows: [...itemList],
                },
              ],
            };
          }
        }
      }
      return mergedData;
    },
    [data, serverSelections]
  );

  const getAvailableCreditLimit = useStableCallback(() => {
    requestMaker({
      method: "post",
      url: configData.GetAvailableCreditLimit,
      data: { store: store?.id },
    })
      .then((res) => {
        const totalCost = res?.data?.totalCost ?? 0;
        dispatch(calculateAvailableCreditLimit(totalCost));
      })
      .catch((err) => {
        console.log(err);
      });
  });

  const checkPOGItemEquality = (pog, selectedItems, lookupKey) => {
    let lookup = null;
    let isUnique = true;
    let lookupList = [];
    const pogItems = pog?.subRows ?? [];
    const mergedItems = pogItems.map((pi) => {
      const updatedItem = selectedItems.find((i) => i?.id === pi?.id);
      if (updatedItem) {
        return updatedItem;
      }
      return pi;
    });
    for (let i = 0; i < mergedItems.length; i++) {
      const item = mergedItems[i];
      let lookupValue = item[lookupKey];
      if (lookupKey === "ReleaseDateTime") {
        lookupValue = dayjs(lookupValue).toJSON();
      }
      if (Boolean(item.Selected)) {
        if (isUnique) {
          if (!lookupList.length && lookupValue) {
            lookupList.push(lookupValue);
          }
          if (!lookupList.includes(lookupValue) || !lookupValue) {
            isUnique = false;
            break;
          }
        } else {
          break;
        }
      }
    }
    if (isUnique) {
      lookup = lookupList[0] || null;
    }
    return lookup;
  };

  // Pog screen - No need to pass any arguments to this function.
  // Order Screen - Save dates pass date only and for delete dates pass date null with isDeleteDate true.
  const saveData = useStableCallback(
    (date = null, isDeleteDate = false, poDetails = {}, mergeItems = []) => {
      const scopeList = Object.keys(serverSelections) ?? [];
      const deltaScopes = Object.keys(deltaServerSelections) ?? [];
      if (!scopeList.length && !deltaScopes.length) {
        return false;
      }
      let updatedData = {};
      let updatedItems = [];
      // For New Selections
      for (let i = 0; i < scopeList.length; i++) {
        const currentScope = scopeList[i];
        const pogList = Object.keys(serverSelections[currentScope]);
        // POGs
        for (let j = 0; j < pogList.length; j++) {
          const pogId = pogList[j];
          let currentPogDetails = data[currentScope]?.find(
            (pog) => String(pog?.id) === String(pogId)
          );
          if (currentPogDetails) {
            currentPogDetails.Selected = 1;
            if (date) {
              currentPogDetails.ReleaseDateTime = date;
            }
            if (isDeleteDate) {
              currentPogDetails.ReleaseDateTime = null;
            }
            if (!isEmpty(poDetails)) {
              currentPogDetails.PONumber = poDetails.customPONumber;
            }
            const pogItemIds = serverSelections[currentScope][pogId] ?? [];
            const updatedItemList = [];
            //POG ITEMs
            for (let k = 0; k < pogItemIds.length; k++) {
              const pogItemId = pogItemIds[k];
              let currentItemDetails = currentPogDetails?.subRows?.find(
                (item) => String(item?.id) === String(pogItemId)
              );
              if (currentItemDetails) {
                if (ENTITY_TYPES.planogram === currentEntity) {
                  currentItemDetails.Selected = 1;
                  updatedItemList.push(currentItemDetails);
                  updatedItems.push({
                    id: currentItemDetails?.id,
                    storeId: store?.id,
                    storeItemId: currentItemDetails?.Item_Nbr,
                    storePlanogramId: currentItemDetails?.PlanogramNumber,
                    multiplier: currentItemDetails?.multiplier,
                    plannerNotes: currentItemDetails?.plannernotesid,
                    plannerAction: Boolean(currentItemDetails?.PlannerAction),
                    retailerNotes: currentItemDetails?.retailernotesid,
                    retailerAction: Boolean(currentItemDetails?.RetailerAction),
                    userId: user?.email,
                    recUniqueID: currentItemDetails?.recUniqueId,
                    selected: currentItemDetails?.Selected,
                    adjQty: Number(currentItemDetails?.adjqty),
                    totalCost: Number(
                      prettyNumericRound(currentItemDetails?.totalCost || 0)
                    ),
                  });
                } else {
                  currentItemDetails.Selected = 1;
                  if (isDeleteDate) {
                    currentItemDetails.ReleaseDateTime = null;
                  }
                  if (date) {
                    currentItemDetails.ReleaseDateTime = date;
                  }
                  if (!isEmpty(poDetails)) {
                    currentItemDetails.PONumber = poDetails.customPONumber;
                    currentItemDetails.generatePOBy = poDetails.generatePOBy;
                  }
                  if (mergeItems.length) {
                    const mergeItem = mergeItems.find(
                      (i) => i?.id === currentItemDetails?.id
                    );
                    if (mergeItem) {
                      currentItemDetails.PONumber = mergeItem?.PONumber;
                    }
                  }
                  const tier = getTierLevelForQty(currentItemDetails);
                  const pogCost = getTierBasedCost(currentItemDetails, tier);
                  updatedItemList.push(currentItemDetails);
                  updatedItems.push({
                    id: currentItemDetails?.id,
                    storeId: store?.id,
                    storeItemId: currentItemDetails?.Item_Nbr,
                    planogramNumber: currentItemDetails?.PlanogramNumber,
                    userId: user?.email,
                    recUniqueID: currentItemDetails?.recUniqueId,
                    selected: currentItemDetails.Selected,
                    releaseDateTime: currentItemDetails?.ReleaseDateTime,
                    customPONumber: currentItemDetails.PONumber,
                    generatePOBy:
                      currentItemDetails.generatePOBy ===
                      GeneratePOByMap[0].label
                        ? "DEPT"
                        : currentItemDetails.generatePOBy,
                    PONumber: currentItemDetails.PONumber,
                    orderQty: currentItemDetails.orderQty,
                    orderCost: currentItemDetails.totalCost,
                    cost: pogCost,
                    short_description: currentItemDetails.short_description,
                    rdcQty: currentItemDetails.RdcQty,
                    upc: currentItemDetails.UPC,
                    model: currentItemDetails.Model,
                    releaseStatus: currentItemDetails.ReleaseStatus,
                    createdDateTime: currentItemDetails.CreatedDateTime,
                    lastUpdatedDateTime: currentItemDetails.LastUpdatedDateTime,
                    createdBy: currentItemDetails.CreatedBy,
                    lastUpdatedBy: currentItemDetails.LastUpdatedBy,
                  });
                }
              }
            }

            if (ENTITY_TYPES.orders === currentEntity) {
              // Update POG Basis of generated PO Number at backend.
              if (mergeItems.length) {
                const PONumber = checkPOGItemEquality(
                  currentPogDetails,
                  updatedItemList,
                  "PONumber"
                );
                currentPogDetails.PONumber = PONumber;
              }
              // Update POG Basis of date and status.
              const releaseDateTime = checkPOGItemEquality(
                currentPogDetails,
                updatedItemList,
                "ReleaseDateTime"
              );
              currentPogDetails.ReleaseDateTime = releaseDateTime;
              const releaseStatus = checkPOGItemEquality(
                currentPogDetails,
                updatedItemList,
                "ReleaseStatus"
              );
              currentPogDetails.ReleaseStatus = releaseStatus;
            }

            if (has(updatedData, currentScope)) {
              updatedData = {
                ...updatedData,
                [currentScope]: [
                  ...updatedData[currentScope],
                  {
                    ...currentPogDetails,
                    subRows: [...updatedItemList],
                  },
                ],
              };
            } else {
              updatedData = {
                ...updatedData,
                [currentScope]: [
                  {
                    ...currentPogDetails,
                    subRows: [...updatedItemList],
                  },
                ],
              };
            }
          }
        }
      }

      // For Delta Server Selections
      for (let ds = 0; ds < deltaScopes.length; ds++) {
        const deltaScope = deltaScopes[ds];
        const deltaPogs = originalData[deltaScope] ?? [];
        if (deltaScopes?.includes(deltaScope)) {
          for (let dp = 0; dp < deltaPogs.length; dp++) {
            const deltaPog = deltaPogs[dp];
            if (has(deltaServerSelections[deltaScope], deltaPog?.id)) {
              const deltaItems = deltaPog?.subRows ?? [];
              for (let di = 0; di < deltaItems.length; di++) {
                let deltaItem = { ...deltaItems[di] };
                const deltaItemIds =
                  deltaServerSelections[deltaScope][deltaPog?.id] ?? [];
                if (deltaItemIds?.includes(deltaItem?.id)) {
                  if (!date && !isDeleteDate) {
                    deltaItem = {
                      id: deltaItem?.id,
                      storeId: store?.id,
                      storeItemId: deltaItem?.Item_Nbr,
                      storePlanogramId: deltaItem?.PlanogramNumber,
                      multiplier: deltaItem?.multiplier,
                      plannerNotes: deltaItem?.plannernotesid,
                      plannerAction: Boolean(deltaItem?.PlannerAction),
                      retailerNotes: deltaItem?.retailernotesid,
                      retailerAction: Boolean(deltaItem?.RetailerAction),
                      userId: user?.email,
                      recUniqueID: deltaItem?.recUniqueId,
                      selected: 0,
                      adjQty: Number(deltaItem?.adjqty),
                      adjcost: Number(
                        prettyNumericRound(deltaItem?.adjcost || 0)
                      ),
                    };
                  } else {
                    deltaItem = {
                      id: deltaItem?.id,
                      storeId: store?.id,
                      storeItemId: deltaItem?.Item_Nbr,
                      planogramNumber: deltaItem?.PlanogramNumber,
                      userId: user?.email,
                      recUniqueID: deltaItem?.recUniqueId,
                      selected: 0,
                      releaseDateTime: null,
                    };
                  }
                  updatedItems.push(deltaItem);
                }
              }
            }
          }
        }
      }

      return [updatedData, updatedItems];
    }
  );

  const handleSave = useStableCallback(() => {
    dispatch(setGlobalLoader(true));
    const [updatedData, updatedItems] = saveData(null);
    requestMaker({
      method: "post",
      url: configData.SavePlanogram,
      data: updatedItems,
    })
      .then(() => {
        const newData = getMergedData(updatedData);
        const updatedSelections = getServerPreSelections(newData);
        setOriginalData(newData);
        setOriginalServerSelections(updatedSelections);
        setServerSelections(updatedSelections);
        getAvailableCreditLimit();
        dispatch(
          showNotifier({
            message: "Planogram data is updated successfully",
          })
        );
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const handleReset = useStableCallback(() => {
    setUnselectedPogs(originalUnselectedPogs);
    setData(originalData);
    setServerSelections(originalServerSelections);
    setFlushStateKey((prevKey) => prevKey + 1);
    setResetFooterKey((prevState) => prevState + 1);
    dispatch(setAdjQtyError(null));
    dispatch(setPrevAdjQty(null));
  });

  const handleCreatePONumbers = useStableCallback((details) => {
    dispatch(setGlobalLoader(true));
    const [, updatedItems] = saveData(null, false, details);
    requestMaker({
      method: "post",
      url: configData.CreatePONumber,
      data: updatedItems,
    })
      .then((res) => {
        const [updatedData] = saveData(null, false, details, res?.data ?? []);
        const newData = getMergedData(updatedData);
        const updatedSelections = getServerPreSelections(newData);
        setOriginalData(newData);
        setOriginalServerSelections(updatedSelections);
        setServerSelections(updatedSelections);
        setResetFooterKey((prevState) => prevState + 1);
        dispatch(
          showNotifier({
            message: "Saved changes to PO number type",
          })
        );
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const checkPOGenerated = useStableCallback(() => {
    const scopeList = Object.keys(serverSelections) ?? [];
    let isAllPOGenerated = true;
    for (let i = 0; i < scopeList.length; i++) {
      const currentScope = scopeList[i];
      const pogList = Object.keys(serverSelections[currentScope]);
      // POGs
      for (let j = 0; j < pogList.length; j++) {
        const pogId = pogList[j];
        if (!isAllPOGenerated) {
          break;
        }
        let currentPogDetails = data[currentScope]?.find(
          (pog) => String(pog?.id) === String(pogId)
        );

        if (currentPogDetails) {
          const subRows = currentPogDetails?.subRows || [];
          const selectedItems =
            serverSelections[currentScope][currentPogDetails?.id] || [];
          const poNumbers = map(subRows, (i) => {
            if (
              selectedItems.includes(i?.id) &&
              (!!i?.PONumber || !!i.customPONumber)
            ) {
              return i.id;
            }
            return undefined;
          })?.filter((i) => !!i);
          const selectionsCount =
            serverSelections[currentScope][pogId].length - 1;

          if (poNumbers.length === selectionsCount) {
            isAllPOGenerated = true;
          } else {
            isAllPOGenerated = false;
            break;
          }
        }
      }
    }
    return isAllPOGenerated;
  });

  const handleSaveDates = useStableCallback((date) => {
    const isPOAlreadyGenerated = checkPOGenerated();
    if (!isPOAlreadyGenerated) {
      return dispatch(
        showNotifier({
          message:
            "PO Number for some items are missing. Please generate PO Number first.",
          severity: "error",
        })
      );
    }
    dispatch(setGlobalLoader(true));
    const [updatedData, updatedItems] = saveData(date);
    requestMaker({
      method: "post",
      url: configData.SaveDates,
      data: updatedItems,
    })
      .then(() => {
        const newData = getMergedData(updatedData);
        const updatedSelections = getServerPreSelections(newData);
        setOriginalData(newData);
        setOriginalServerSelections(updatedSelections);
        setServerSelections(updatedSelections);
        getAvailableCreditLimit();
        setResetFooterKey((prevState) => prevState + 1);
        dispatch(
          showNotifier({
            message: "Release dates saved successfully",
          })
        );
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const handleDeleteDates = useStableCallback(() => {
    const [updatedData, updatedItems] = saveData(null, true);
    const selectedItems =
      updatedItems?.filter((i) => Boolean(i.selected)) ?? [];
    requestMaker({
      method: "post",
      url: configData.DeleteDates,
      data: selectedItems,
    })
      .then(() => {
        const newData = getMergedData(updatedData);
        const updatedSelections = getServerPreSelections(newData);
        setOriginalData(newData);
        setOriginalServerSelections(updatedSelections);
        setServerSelections(updatedSelections);
        getAvailableCreditLimit();
        setResetFooterKey((prevState) => prevState + 1);
        dispatch(
          showNotifier({
            message: "Release dates deleted successfully",
          })
        );
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  });

  const disableAll = useMemo(
    () =>
      store?.pending ||
      store?.isArchived ||
      !hasPermission({ scopes: [SCOPES.canEdit] }),
    [store, hasPermission]
  );

  const selectionsPresent = useMemo(() => {
    return (
      (Object.keys(serverSelections).length === 0 &&
        Object.keys(deltaServerSelections).length === 0) ||
      isEmpty(serverSelections) ||
      disableAll
    );
  }, [serverSelections, deltaServerSelections, disableAll]);

  return (
    <Container id="page-container" maxWidth="xl" sx={{ paddingY: 2 }}>
      <Stack spacing={2}>
        <DiscountSearch
          disc={handleViewSearchBy}
          getFilters={handleFilterData}
          getData={handleSearchData}
          entityType={currentEntity}
        />
        {!isEmpty(data) && (
          <OSOAccordion
            key={flushStateKey}
            fetchedData={data}
            viewBy={viewPogBy}
            filterData={filterData}
            serverSelections={serverSelections}
            updateSelections={updateSelections}
            selectAllAccordion={handleSelectAllAccordion}
            selectAllSubAccordion={handleSelectAllSubAccordion}
            globalMultiplierChange={handleGlobalMultiplierChange}
            getProcessedData={handleProcessedData}
          >
            <ActionsFooter
              key={resetFooterKey}
              onSave={handleSave}
              onReset={handleReset}
              onCreatePONumbers={handleCreatePONumbers}
              onSaveDates={handleSaveDates}
              onDeleteDates={handleDeleteDates}
              disableSave={selectionsPresent}
            />
          </OSOAccordion>
        )}
      </Stack>
    </Container>
  );
};

export default ManageStore;
